import { generateJSON, type AnyExtension } from "@tiptap/core";

import { normalizeTemplateContent, type JSONNode } from "utils/templates";

/**
 * Paste is deliberately narrower than the general template vocabulary (which
 * also supports images, columns, signatures, field tokens, code, strike,
 * links, etc. for authored/imported templates): pasted content may only
 * become heading levels 1-3, paragraphs, bullet/ordered lists, tables, bold,
 * italic, underline, and text color. Everything else — code, strikethrough,
 * links, columns, images, ... — is unwrapped (its text kept, formatting
 * dropped) rather than carried in from an external source.
 *
 * DMS-2027: tables were excluded here for a while, which silently flattened a
 * table pasted from Word/Excel/a web page into one bare paragraph per cell —
 * each cell's own parseHTML (Table/Cell/Header/Row extensions) already parses
 * a plain <table>/<tr>/<td> correctly on its own; this allowlist was the only
 * thing discarding the result afterwards.
 */
const PASTE_ALLOWED_NODES = new Set([
  "heading",
  "paragraph",
  "bulletList",
  "orderedList",
  "listItem",
  "table",
  "tableRow",
  "tableCell",
  "tableHeader",
  "text",
  "hardBreak",
]);
const PASTE_ALLOWED_MARKS = new Set(["bold", "italic", "underline", "textStyle"]);

function restrictMarks(marks: JSONNode["marks"]): JSONNode["marks"] {
  if (!Array.isArray(marks)) return undefined;
  const kept = marks
    .filter((m) => !!m.type && PASTE_ALLOWED_MARKS.has(m.type))
    .map((m) => (m.type === "textStyle" && m.attrs?.color ? { type: "textStyle", attrs: { color: m.attrs.color } } : { type: m.type! }));
  return kept.length ? kept : undefined;
}

function restrictNodes(nodes: JSONNode[]): JSONNode[] {
  const out: JSONNode[] = [];
  for (const node of nodes) {
    if (node.type === "text") {
      const marks = restrictMarks(node.marks);
      const next: JSONNode = { type: "text", text: node.text };
      if (marks) next.marks = marks;
      out.push(next);
      continue;
    }
    if (node.type && PASTE_ALLOWED_NODES.has(node.type)) {
      const next: JSONNode = { ...node };
      if (Array.isArray(node.content)) next.content = restrictNodes(node.content);
      out.push(next);
      continue;
    }
    // Unsupported-for-paste node (code block, columns, image, signature, ...):
    // unwrap rather than drop, so its text still comes through as plain content.
    if (Array.isArray(node.content)) out.push(...restrictNodes(node.content));
  }
  return out;
}

/**
 * Custom clipboard MIME type carrying a copied node's raw TipTap JSON — set by
 * ContentItemMenu's "Copy to clipboard" action (useContentItemActions.tsx) alongside the
 * browser's normal HTML/text clipboard data, and read back on paste (useBlockEditor.ts) before
 * any HTML-based reconstruction is attempted. Some nodes can't round-trip through HTML at all —
 * ImageBlock's parseHTML() unconditionally returns [] ("prevent importing images"), so neither
 * the restricted external-paste path below nor ProseMirror's own native data-pm-slice
 * reconstruction (which also parses HTML back through the same schema rules) can rebuild an
 * image node from HTML. JSON round-trips any node type with perfect fidelity because it never
 * touches HTML/DOM parsing at all.
 */
export const INTERNAL_NODE_CLIPBOARD_MIME = "application/x-defectwise-node-json";

/**
 * True when `html` is clipboard content ProseMirror itself produced (our own
 * "Copy to clipboard" node action, or a native Ctrl+C/Ctrl+X inside any
 * editor in this app) — identifiable by the `data-pm-slice` attribute
 * prosemirror-view stamps onto its own clipboard HTML (see clipboard.ts in
 * prosemirror-view) and reads back to reconstruct the exact node. That
 * reconstruction works across any editor instance sharing this app's schema,
 * not just the one it was copied from, so this content should bypass the
 * external-paste restriction below entirely rather than being routed through
 * it — PASTE_ALLOWED_NODES doesn't include image/table/columns/signature/
 * mention, so an internally-copied node of one of those types would
 * otherwise be silently dropped instead of pasted.
 */
export function isInternalClipboardHtml(html: string): boolean {
  return html.includes("data-pm-slice");
}

/**
 * Word/Excel (and Office generally) wrap a copied selection in a full HTML document with a
 * `<head>` full of mso-/w:-prefixed style and XML metadata blocks (hundreds of lines) plus
 * `<link>`s to local temp files — none of it meaningful content. Office also marks the real
 * selection with `<!--StartFragment-->`/`<!--EndFragment-->` comments, but *where* those land is
 * inconsistent between apps: Word wraps the whole `<table>` in them, Excel places them inside the
 * `<table>` tag around just the rows — slicing to "between the comments" would silently strip
 * Excel's own table wrapper. Stripping only the unambiguous junk (`<head>`) sidesteps that
 * entirely: it's dropped regardless of source, and every actual content tag — including a
 * <table> wrapper wherever it sits relative to the fragment comments — stays exactly where it
 * was written.
 */
function stripHeadBlock(html: string): string {
  return html.replace(/<head[^>]*>[\s\S]*?<\/head>/i, "");
}

/**
 * Converts pasted HTML into normalized TipTap JSON using the editor's real
 * schema (via `extensions`), then restricts it to the paste-specific
 * vocabulary above. Never throws: clipboard HTML from unpredictable sources
 * (Word, Excel, etc.) that can't be made sense of just yields [], letting the
 * caller fall back to a default (e.g. plain-text) paste.
 */
export function htmlToNormalizedContent(html: string, extensions: AnyExtension[]): JSONNode[] {
  try {
    const json = generateJSON(stripHeadBlock(html), extensions);
    return restrictNodes(normalizeTemplateContent(json));
  } catch {
    return [];
  }
}

/** Minimal shape of `ClipboardEvent.clipboardData` this only needs `getData` from. */
export type ClipboardDataLike = { getData(format: string): string } | null | undefined;

export type ResolvedPaste =
  /** Insert this content directly (already-normalized TipTap JSON). */
  | { kind: "insert"; content: unknown }
  /** Don't intercept — let ProseMirror's own native paste handling run instead. */
  | { kind: "deferToNative" }
  /** Nothing usable found — caller should return false and let the browser's default happen. */
  | null;

/**
 * Resolves what a paste event should do, in priority order — see the comment on
 * INTERNAL_NODE_CLIPBOARD_MIME and isInternalClipboardHtml above for why each of these three
 * paths exists. Pure function of the clipboard data (no DOM/editor access) so it's testable
 * without needing to simulate a real browser paste through an actual EditorView.
 */
export function resolvePastedContent(clipboardData: ClipboardDataLike, extensions: AnyExtension[]): ResolvedPaste {
  const internalNodeJson = clipboardData?.getData(INTERNAL_NODE_CLIPBOARD_MIME);
  if (internalNodeJson) {
    try {
      return { kind: "insert", content: JSON.parse(internalNodeJson) };
    } catch {
      // Malformed/unreadable — fall through to the HTML-based paths below.
    }
  }

  const html = clipboardData?.getData("text/html");
  if (!html) return null;
  if (isInternalClipboardHtml(html)) return { kind: "deferToNative" };

  const content = htmlToNormalizedContent(html, extensions);
  if (!content.length) return null;

  return { kind: "insert", content };
}
